import React from 'react'
import {Helmet} from 'react-helmet'
import {useLocation} from 'react-router-dom'
import {
  pathSegments,
  stripLocaleFromSegments,
  withLocalePath,
} from 'i18n/localePaths'
import {
  SUPPORTED_LOCALES,
  appLocaleToBcp47,
  HREFLANG_X_DEFAULT_LOCALE,
} from 'i18n/locales'
import {useI18n} from 'i18n/I18nContext'
import {SITE_ORIGIN} from './siteMeta'

/** Path after `/:lang` for building locale-prefixed URLs (e.g. `/bg/prices` → `/prices`). */
function pathSuffixAfterLocalePrefix(pathname: string): string {
  const segs = stripLocaleFromSegments(pathSegments(pathname))
  if (!segs.length) return '/'
  return `/${segs.join('/')}`
}

function bcp47ToOgLocale(tag: string): string {
  return tag.replace(/-/g, '_')
}

/**
 * hreflang cluster for supported locales, document language, and og:locale hints.
 * Canonical URLs stay on individual pages (Helmet) to avoid duplicate canonical tags.
 */
export function SeoLanguageAlternates() {
  const location = useLocation()
  const {locale} = useI18n()
  const suffix = pathSuffixAfterLocalePrefix(location.pathname)
  const search = location.search || ''

  return (
    <Helmet>
      <html lang={appLocaleToBcp47(locale)} />
      {SUPPORTED_LOCALES.map((loc) => (
        <link
          key={`hreflang-${loc}`}
          rel="alternate"
          hrefLang={appLocaleToBcp47(loc)}
          href={`${SITE_ORIGIN}${withLocalePath(loc, suffix)}${search}`}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${SITE_ORIGIN}${withLocalePath(HREFLANG_X_DEFAULT_LOCALE, suffix)}${search}`}
      />
      <meta
        property="og:locale"
        content={bcp47ToOgLocale(appLocaleToBcp47(locale))}
      />
      {SUPPORTED_LOCALES.filter((l) => l !== locale).map((l) => (
        <meta
          key={`og-alt-${l}`}
          property="og:locale:alternate"
          content={bcp47ToOgLocale(appLocaleToBcp47(l))}
        />
      ))}
    </Helmet>
  )
}
